import { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
} from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { SeverityPill } from "../severity-pill";
import { LogDetailModal } from "./log-detail-modal";

export const LogsListResults = ({
  logsInfo = [],
  loading = false,
  error,
  emptyResults,
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onRowsPerPageChange,
  ...rest
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const materialUIPage = currentPage - 1;

  const handlePageChange = (event, newPage) => {
    const backendPage = newPage + 1;
    onPageChange(backendPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = Number.parseInt(event.target.value, 10);
    onRowsPerPageChange(newPageSize);
  };

  const handleOpenModal = (log) => {
    setSelectedLog(log);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedLog(null);
  };

  const transformFecha = (fechaISO) =>
    new Date(fechaISO).toLocaleDateString("es-AR");

  const transformHora = (fechaISO) =>
    new Date(fechaISO).toTimeString().split(" ")[0];

  const truncateString = (value) => {
    if (typeof value !== "string") return "";
    return value.length > 100 ? value.slice(0, 100) + "..." : value;
  };

  const renderLogLevel = (level) => {
    switch (level?.toLowerCase()) {
      case "information":
        return <SeverityPill color="levelInfo">Información</SeverityPill>;
      case "error":
        return <SeverityPill color="levelError">Error</SeverityPill>;
      case "warning":
        return <SeverityPill color="levelWarning">Aviso</SeverityPill>;
      default:
        return <SeverityPill color="default">{level}</SeverityPill>;
    }
  };

  return (
    <Card
      sx={{
        border: "0.5px solid #dddddd",
        padding: "1px",
        boxShadow: "1px 1px #d5d5d5",
      }}
      {...rest}
    >
      <Box sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fuente</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Hora</TableCell>
              <TableCell>Mensaje</TableCell>
              <TableCell>Excepción</TableCell>
              <TableCell align="center">Nivel</TableCell>
              <TableCell align="center">Detalle</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              logsInfo?.length > 0 &&
              logsInfo.map((log, index) => (
                <TableRow hover key={`${log.timestamp}-${index}`}>
                  <TableCell>{log.source}</TableCell>
                  <TableCell>{transformFecha(log.timestamp)}</TableCell>
                  <TableCell>{transformHora(log.timestamp)}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 300,
                      width: 300,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {truncateString(log.messageTemplate || log.message)}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 300,
                      width: 300,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {truncateString(log.exception)}
                  </TableCell>
                  <TableCell
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {renderLogLevel(log.logLevel)}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton onClick={() => handleOpenModal(log)}>
                        <RemoveRedEyeOutlinedIcon color="primary" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Box>

      {!loading && logsInfo?.length === 0 && !emptyResults && (
        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
          <Alert severity="info">
            No se encontraron resultados para la búsqueda
          </Alert>
        </Box>
      )}

      {emptyResults && (
        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
          <Alert severity="info">{emptyResults}</Alert>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && logsInfo?.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              md: "row",
            },
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <TablePagination
            component="div"
            count={totalItems}
            page={materialUIPage}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
            showFirstButton
            showLastButton
          />

          <Typography variant="body2" color="text.secondary">
            Página {currentPage} de {totalPages} | Total: {totalItems} registros
          </Typography>
        </Box>
      )}

      <LogDetailModal
        open={openModal}
        onClose={handleCloseModal}
        log={selectedLog}
        transformFecha={transformFecha}
        transformHora={transformHora}
      />
    </Card>
  );
};

LogsListResults.propTypes = {
  logsInfo: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  emptyResults: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  pageSize: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
};
